import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { colorFor, initials, fmtMin } from "@/lib/format";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-6 fade-up" data-testid={testid || "page-header"}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-50">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Kpi({ label, value, sub, tone, icon: Icon, testid }) {
  const toneCls = tone === "up" ? "text-emerald-400" : tone === "down" ? "text-red-400" : "text-slate-400";
  return (
    <div className="card-surface p-4 fade-up hover:border-violet-500/40 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-violet-400" />}
      </div>
      <div className="num text-2xl font-semibold text-slate-50 mt-2" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${toneCls}`}>{sub}</div>}
    </div>
  );
}

export function Card({ title, actions, children, className = "", testid }) {
  return (
    <div className={`card-surface p-4 fade-up ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center justify-between mb-3 gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</h3>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

const STATUS = {
  connected: ["CONNESSO", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", "bg-emerald-400"],
  connecting: ["CONNESSIONE", "bg-amber-500/15 text-amber-300 border-amber-500/30", "bg-amber-400 pulse-dot"],
  disconnected: ["DISCONNESSO", "bg-red-500/15 text-red-400 border-red-500/30", "bg-red-400"],
  auth_required: ["AUTENTICAZIONE RICHIESTA", "bg-orange-500/15 text-orange-300 border-orange-500/30", "bg-orange-400"],
  demo: ["DEMO · NON COLLEGATO", "bg-sky-500/15 text-sky-300 border-sky-500/30", "bg-sky-400"],
};

export function StatusBadge({ status, testid }) {
  const [label, cls, dot] = STATUS[status] || [status, "bg-slate-500/15 text-slate-300 border-slate-500/30", "bg-slate-400"];
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wide ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

export function Pill({ children, color = "#7C3AED", className = "" }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${className}`} style={{ background: `${color}22`, color }}>
      {children}
    </span>
  );
}

export function Avatar({ name, url, size = 36, color }) {
  const s = { width: size, height: size, fontSize: size * 0.38 };
  if (url) return <img src={url} alt={name} style={s} className="rounded-full object-cover shrink-0" />;
  return (
    <div style={{ ...s, background: color || colorFor(name) }} className="rounded-full shrink-0 flex items-center justify-center font-bold text-white">
      {initials(name)}
    </div>
  );
}

const RANGES = [["today", "Oggi"], ["yesterday", "Ieri"], ["day", "Data specifica"], ["7d", "Ultimi 7 giorni"], ["30d", "Ultimi 30 giorni"], ["90d", "Ultimi 90 giorni"],
  ["week", "Settimana corrente"], ["month", "Mese corrente"], ["prev_month", "Mese precedente"], ["m:", "Mese specifico"], ["year", "Anno corrente"],
  ["all", "Tutto lo storico"], ["custom", "Intervallo personalizzato"]];

export function RangeFilter({ value, onChange, custom = {}, setCustom = () => {}, testid = "range-filter" }) {
  const isMonth = value?.startsWith("m:");
  const cur = new Date().toISOString().slice(0, 7);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <select value={isMonth ? "m:" : value} onChange={(e) => onChange(e.target.value === "m:" ? `m:${cur}` : e.target.value)} className={selectCls} data-testid={`${testid}-select`}>
        {RANGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {isMonth && <Input type="month" className="h-9 w-40 bg-[#0D0E14]" value={value.slice(2)} onChange={(e) => e.target.value && onChange(`m:${e.target.value}`)} data-testid={`${testid}-month`} />}
      {value === "day" && <Input type="date" className="h-9 w-40 bg-[#0D0E14]" value={custom.start || ""} onChange={(e) => setCustom({ start: e.target.value, end: e.target.value })} data-testid={`${testid}-day`} />}
      {value === "custom" && (
        <div className="flex items-center gap-1">
          <Input type="date" className="h-9 w-36 bg-[#0D0E14]" value={custom.start || ""} onChange={(e) => setCustom({ ...custom, start: e.target.value })} data-testid={`${testid}-start`} />
          <span className="text-slate-500">→</span>
          <Input type="date" className="h-9 w-36 bg-[#0D0E14]" value={custom.end || ""} onChange={(e) => setCustom({ ...custom, end: e.target.value })} data-testid={`${testid}-end`} />
        </div>
      )}
    </div>
  );
}

export const fmtDur = (min) => fmtMin(min);
export const fmtHours = (h) => fmtDur(Math.round((h || 0) * 60));

export function Spinner({ className = "" }) {
  return <div className={`flex items-center justify-center py-10 ${className}`}><Loader2 className="h-5 w-5 animate-spin text-violet-400" /></div>;
}

export function Empty({ title = "Nessun dato", text, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
      {Icon && <Icon className="h-8 w-8 text-slate-600 mb-3" />}
      <p className="text-sm font-semibold text-slate-300">{title}</p>
      {text && <p className="text-xs text-slate-500 mt-1 max-w-sm">{text}</p>}
    </div>
  );
}

export const tooltipStyle = { contentStyle: { background: "#0D0E14", border: "1px solid #232738", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#94A3B8" } };

export function Table({ head, children, testid }) {
  return (
    <div className="overflow-x-auto thin-scroll" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#232738] text-left">
            {head.map((h, i) => <th key={i} className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody className="[&>tr]:border-b [&>tr]:border-[#1A1D2B] [&>tr:hover]:bg-white/[0.02]">{children}</tbody>
      </table>
    </div>
  );
}

export const Td = ({ children, className = "", ...p }) => <td className={`px-3 py-2.5 whitespace-nowrap text-slate-300 ${className}`} {...p}>{children}</td>;

export const selectCls = "h-9 rounded-md border border-[#232738] bg-[#0D0E14] px-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500";
